import { useState } from 'react'
import type React from 'react'
import { UploadCard } from '../ui/upload-card'

interface FileUploadProps {
  mode: 'image' | 'document' | 'csv'
  accept: string
  buttonLabel: string
  helperText?: string | React.ReactNode
  maxSizeMB: number
  errorStrings: {
    fileCount: string
    fileType: string
    fileSize: string
    unreadable: string
    password?: string
    empty?: string
    generic: string
    linkDropNotAllowed?: string
  }
  ariaLabel: string
  onFileSelect: (file: File | null) => void
  currentFile: File | null
  error?: string
  variant?: 'default' | 'link-in-dropzone'
  helperSubtext?: string
  templateUrl?: string
  templateName?: string
  onRemove?: () => void
  externalUploading?: boolean
  externalError?: string
  requiredWidth?: number
  requiredHeight?: number
}

interface ValidationError {
  type:
    | 'count'
    | 'type'
    | 'size'
    | 'corrupted'
    | 'dimensions'
    | 'password'
    | 'empty'
    | 'linkDrop'
  message: string
}

export function FileUpload({
  mode,
  accept,
  buttonLabel,
  helperText,
  maxSizeMB,
  errorStrings,
  ariaLabel,
  onFileSelect,
  currentFile,
  error,
  variant = 'default',
  helperSubtext,
  templateUrl,
  templateName = 'Download Template',
  onRemove,
  externalUploading = false,
  externalError,
  requiredWidth = 512,
  requiredHeight = 512,
}: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null)

  const validateFile = async (file: File): Promise<ValidationError | null> => {
    if (mode === 'image') {
      if (!file.type.includes('png'))
        return { type: 'type', message: errorStrings.fileType }
      const buffer = await file.arrayBuffer()
      const uint8Array = new Uint8Array(buffer)
      const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
      if (
        uint8Array.length < 8 ||
        !pngSignature.every((b, i) => uint8Array[i] === b)
      )
        return { type: 'type', message: errorStrings.fileType }
      if (file.size > maxSizeMB * 1024 * 1024)
        return { type: 'size', message: errorStrings.fileSize }
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          if (img.width !== requiredWidth || img.height !== requiredHeight)
            resolve({
              type: 'dimensions',
              message: `Image must be ${requiredWidth}×${requiredHeight}px.`,
            })
          else resolve(null)
        }
        img.onerror = () =>
          resolve({ type: 'corrupted', message: errorStrings.unreadable })
        img.src = URL.createObjectURL(file)
      })
    } else if (
      mode === 'csv' ||
      (mode === 'document' && accept.includes('csv'))
    ) {
      if (!file.name.toLowerCase().endsWith('.csv'))
        return { type: 'type', message: errorStrings.fileType }
      const validCsvMimeTypes = ['text/csv', 'application/csv', 'text/plain']
      if (
        file.type &&
        !validCsvMimeTypes.some((t) => file.type.includes(t)) &&
        !file.type.includes('csv')
      ) {
        const suspiciousMimeTypes = [
          'image/',
          'video/',
          'audio/',
          'application/pdf',
        ]
        if (suspiciousMimeTypes.some((t) => file.type.includes(t)))
          return { type: 'type', message: errorStrings.fileType }
      }
      if (file.size > maxSizeMB * 1024 * 1024)
        return { type: 'size', message: errorStrings.fileSize }
      if (file.size === 0)
        return {
          type: 'empty',
          message: errorStrings.empty || 'The file appears to be empty.',
        }
      return null
    } else {
      if (
        file.type !== 'application/pdf' &&
        !file.name.toLowerCase().endsWith('.pdf')
      )
        return { type: 'type', message: errorStrings.fileType }
      if (file.size > maxSizeMB * 1024 * 1024)
        return { type: 'size', message: errorStrings.fileSize }
      if (file.size === 0)
        return {
          type: 'empty',
          message: errorStrings.empty || 'The file appears to be empty.',
        }
      try {
        const buffer = await file.arrayBuffer()
        const uint8Array = new Uint8Array(buffer)
        const pdfSignature = [0x25, 0x50, 0x44, 0x46]
        if (
          uint8Array.length < 4 ||
          !pdfSignature.every((byte, index) => uint8Array[index] === byte)
        )
          return { type: 'corrupted', message: errorStrings.unreadable }
        const text = new TextDecoder().decode(uint8Array.slice(0, 1024))
        if (text.includes('/Encrypt'))
          return {
            type: 'password',
            message: errorStrings.password || 'This PDF is password protected.',
          }
        return null
      } catch {
        return { type: 'corrupted', message: errorStrings.unreadable }
      }
    }
  }

  const processFile = async (file: File | null) => {
    if (!file) {
      onFileSelect(null)
      setValidationError(null)
      return
    }
    setIsUploading(true)
    setValidationError(null)
    const validationResult = await validateFile(file)
    if (validationResult) {
      setValidationError(validationResult)
      setIsUploading(false)
    } else {
      setValidationError(null)
      onFileSelect(file)
      setIsUploading(false)
    }
  }

  const finalHelperText = helperText || helperSubtext || ''
  const templateLink = templateUrl
    ? { href: templateUrl, label: templateName }
    : undefined

  return (
    <UploadCard
      accept={accept}
      maxSizeMB={maxSizeMB}
      buttonLabel={buttonLabel}
      helperText={finalHelperText}
      templateLink={templateLink}
      ariaLabel={ariaLabel}
      onFileSelect={processFile}
      currentFile={currentFile}
      errorText={validationError?.message || externalError || error}
      isUploading={isUploading || externalUploading}
      variant={variant}
      onRemove={onRemove}
    />
  )
}
